import { MagnifyingGlassIcon } from '@phosphor-icons/react';
import * as React from 'react';
import { css, html } from 'react-strict-dom';

import { Api, useApiCallable } from '../../../api';
import { getApiErrorMessage } from '../../../api/hub-api-request';
import { Modal, Stack, Text } from '../../../components';
import { color } from '../../../constants/color.css';
import { useSelectedCompany } from '../../../contexts';
import { type Idea } from '../board';

type Props = {
  idea: Idea;
  invalidate: () => unknown;
  onClose: () => void;
};

export default function LinkServiceOrderModal({
  idea,
  invalidate,
  onClose,
}: Props) {
  const company = useSelectedCompany();
  const callApi = useApiCallable();
  const api = Api.ServiceOrder.useReadCollectionServiceOrders(company.id);

  const [search, setSearch] = React.useState('');
  const [linking, setLinking] = React.useState(false);

  const orders = api.data?.['hydra:member'] ?? [];
  const needle = search.trim().toLowerCase();
  const filtered = needle
    ? orders.filter((order) =>
        `${order.name} ${order.referenceNumber ?? ''}`
          .toLowerCase()
          .includes(needle)
      )
    : orders;

  const link = async (serviceOrderIri: string) => {
    if (linking) return;
    setLinking(true);
    try {
      await callApi(`/wishlist-ideas/${idea.id}/link-service-order`, {
        method: 'POST',
        jsonBody: { serviceOrder: serviceOrderIri },
      });
      onClose();
      invalidate();
    } catch (err) {
      alert(getApiErrorMessage(err));
      setLinking(false);
    }
  };

  return (
    <Modal title="Link to service order" onClose={onClose} fullScreenOnMobile>
      <Stack padH={1.5} padV={1.5} gap={1}>
        <html.div style={styles.search}>
          <MagnifyingGlassIcon size={16} color={color.neutral40} />
          <html.input
            style={styles.input}
            value={search}
            placeholder="Search service orders"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setSearch(e.target.value)
            }
          />
        </html.div>

        {api.error ? (
          <Text typeface="body.small.regular" color="neutral60">
            Could not load service orders.
          </Text>
        ) : !api.data ? (
          <Text typeface="body.small.regular" color="neutral60">
            Loading service orders…
          </Text>
        ) : filtered.length === 0 ? (
          <Text typeface="body.small.regular" color="neutral60">
            No service orders found.
          </Text>
        ) : (
          <html.div style={styles.list}>
            {filtered.map((order) => (
              <html.button
                key={order.id}
                style={styles.row}
                disabled={linking}
                onClick={() => link(order['@id'])}
              >
                <Stack gap={0.25} style={styles.rowText}>
                  <Text typeface="body.medium" color="neutral100">
                    {order.name}
                  </Text>
                  <Text typeface="body.small.regular" color="neutral60">
                    {order.referenceNumber
                      ? `#SO-${order.referenceNumber}`
                      : 'No reference'}
                  </Text>
                </Stack>
              </html.button>
            ))}
          </html.div>
        )}
      </Stack>
    </Modal>
  );
}

const styles = css.create({
  search: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 12,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: color.neutral20,
    borderRadius: 8,
  },
  input: {
    flex: 1,
    borderWidth: 0,
    outline: 'none',
    fontSize: 16,
    fontFamily: 'Inter, sans-serif',
    lineHeight: 1.5,
    color: color.neutral100,
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    paddingBlock: 12,
    paddingInline: 8,
    borderWidth: 0,
    borderBottomWidth: 1,
    borderBottomStyle: 'solid',
    borderBottomColor: color.neutral20,
    backgroundColor: color.neutral0,
    textAlign: 'left',
    cursor: 'pointer',
  },
  rowText: {
    flex: 1,
  },
});
